import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import {
  Hero, TrustedBy, Problem, Solution, Applications,
  EaasFlow, WhyTratou, GlobalMap, Testimonials,
} from "@/components/sections/Sections";
import ROICalculator from "@/components/sections/ROICalculator";
import SiteAssessment from "@/components/sections/SiteAssessment";
import { api } from "@/lib/api";

function BlogPreview() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (!posts.length) return null;
  return (
    <section className="py-28" data-testid="blog-preview">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12 gap-4">
          <div>
            <span className="eyebrow">Insights</span>
            <h2 className="heading-section mt-4">From the field.</h2>
          </div>
          <Link to="/blog" className="btn-secondary self-end" data-testid="blog-view-all">
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-tratou-deep group panel-hover"
            >
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-video overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                </div>
                <div className="p-6">
                  <p className="eyebrow">{p.category} · {p.read_time}</p>
                  <h3 className="mt-3 font-display font-bold text-xl leading-tight group-hover:text-tratou-green transition">{p.title}</h3>
                  <p className="mt-3 text-tratou-dim text-sm">{p.excerpt}</p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="py-32 relative overflow-hidden" data-testid="cta-banner">
      <div className="absolute inset-0 -z-10 bg-grid bg-grid-fade opacity-50" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
        <h2 className="font-display font-bold tracking-tighter leading-[0.95]" style={{ fontSize: "clamp(3rem, 9vw, 7rem)" }}>
          Ready to <span className="text-tratou-green">power</span><br /> your site?
        </h2>
        <p className="mt-8 text-tratou-dim text-lg max-w-xl mx-auto">
          Get a tailored MBESS proposal in under 24 hours. No diesel logistics. No CapEx. Just kilowatt-hours.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/contact?intent=quote" className="btn-primary" data-testid="cta-banner-quote">Request a Quote <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/contact?intent=demo" className="btn-secondary" data-testid="cta-banner-demo">Book a Live Demo</Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <Layout>
      <Hero />
      <TrustedBy />
      <SiteAssessment />
      <Problem />
      <Solution />
      <Applications />
      <EaasFlow />
      <WhyTratou />
      <ROICalculator />
      <GlobalMap />
      <Testimonials />
      <BlogPreview />
      <CtaBanner />
    </Layout>
  );
}
